import { Select } from 'antd';
import type { RefSelectProps } from 'antd/es/select';
import classNames from 'classnames';
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { UpdateProjectForm } from '@/interface';

import { updateProjectRequest } from '@/redux/slices';
import { AppDispatch, RootState } from '@/redux/store';

const projectStatuses = [
  { label: 'Active', value: 'active' },
  // { label: 'On Review', value: 'on_review' },
  { label: 'Closed', value: 'closed' },
  { label: 'Paused', value: 'paused' },
  { label: 'Completed', value: 'completed' },
];

const statusClasses: Record<string, string> = {
  active: 'bg-green-100 text-green-700 border border-green-300',
  on_review: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
  closed: 'bg-red-100 text-red-700 border border-red-300',
  paused: 'bg-gray-200 text-gray-700 border border-gray-400',
  completed: 'bg-blue-100 text-blue-700 border border-blue-300',
};

interface CustomStatusDropdownProps {
  projectId: string;
  currentStatus: string;
  disabled?: boolean;
}

export default function CustomStatusDropdown({ projectId, currentStatus, disabled = false }: CustomStatusDropdownProps) {
  const { projectDetail } = useSelector((state: RootState) => state.project);
  const dispatch = useDispatch<AppDispatch>();
  const [selectedStatus, setSelectedStatus] = useState<string>(currentStatus);
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<RefSelectProps>(null);
  // const { userToken } = useSelector((state: RootState) => state.auth);
  // const decoded: any = userToken ? jwtDecode(userToken) : null;
  // const userType = decoded?.userType;

  useEffect(() => {
    setSelectedStatus(currentStatus);
  }, [currentStatus]);

  let shouldHide = false;

  if (!projectDetail?.company && !projectDetail?.companyId) {
    shouldHide = true;
  }

  const handleChange = (value: string) => {
    if (disabled) return;
    setSelectedStatus(value);
    const data: UpdateProjectForm = { status: value };
    dispatch(updateProjectRequest({ id: projectId, data }));
    setIsOpen(false);
  };

  const selectedClass = statusClasses[selectedStatus.toLowerCase()] || 'bg-gray-100 text-gray-700 border border-gray-300';

  return (
    <div
      className={classNames(
        'relative w-full sm:w-64 py-4 px-1 transition-all duration-200',
        shouldHide ? 'invisible' : 'visible',
      )}
    >
      <div
        onClick={() => {
          if (!disabled) setIsOpen(true);
        }}
        className={classNames(
          'w-full flex justify-between items-center px-4 py-2 rounded-md text-sm font-medium border',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          selectedClass,
        )}
      >
        <span className="capitalize truncate">
          <span className="text-black">Status </span> :{' '}
          {projectStatuses.find((s) => s.value === selectedStatus)?.label || selectedStatus}
        </span>
        <ChevronDown className="w-4 h-4" />
      </div>

      <Select
        ref={selectRef}
        value={selectedStatus}
        onChange={handleChange}
        open={disabled ? false : isOpen}
        disabled={disabled}
        onDropdownVisibleChange={(visible) => !disabled && setIsOpen(visible)}
        style={{
          position: 'absolute',
          top: 20,
          width: '100%',
          opacity: 0,
          zIndex: 100,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
        options={projectStatuses.map((item) => ({
          value: item.value,
          label: item.label,
        }))}
      />
    </div>
  );
}
